<template>
  <k-panel
    class="col-two-panel"
    title="Links"
    :show-content="showContent"
    @toggle="showContent = $event"
  >
    <template #body>
      <div class="learner-links-panel-body">
        <div v-if="canManageLinks" class="learner-links-actions">
          <template v-if="showGenerateDeliveryProfile">
            <button
              v-if="isGenerating"
              type="button"
              class="btn btn-outlined generate-delivery-profile"
              disabled
            >
              <i class="fa-duotone fa-spinner fa-spin" aria-hidden="true"></i>
              Generating...
            </button>
            <button
              v-else
              type="button"
              class="btn btn-outlined generate-delivery-profile"
              @click="generateDeliveryProfile"
            >
              Generate delivery profile
            </button>
          </template>
          <button
            type="button"
            class="btn btn-primary add-new-link"
            @click="openAddModal"
          >
            Add new <i class="fa-solid fa-plus" aria-hidden="true"></i>
          </button>
        </div>
        <learner-links-list
          :links="links"
          :can-manage="canManageLinks"
          @edit="openEditModal"
          @delete="openDeleteModal"
        />
        <k-modal
          v-if="showLinkModal"
          :show="showLinkModal"
          class="learner-links-modal"
          @close="closeLinkModal"
        >
          <template #header>
            <h3>{{ editingLink ? 'Edit link' : 'Add new link' }}</h3>
          </template>
          <template #body>
            <learner-links-form
              ref="linksFormComponent"
              v-model="linkForm"
              @submit="saveLink"
            />
          </template>
          <template #footer>
            <button type="button" class="btn btn-outlined" @click="closeLinkModal">Cancel</button>
            <button
              v-if="savingLink"
              type="button"
              class="btn btn-primary"
              disabled
            >
              <i class="fa-duotone fa-spinner fa-spin" aria-hidden="true"></i>
              {{ editingLink ? 'Saving changes...' : 'Saving link...' }}
            </button>
            <button
              v-else
              type="button"
              class="btn btn-primary"
              :disabled="disableSaveLink"
              @click="submitLinkForm"
            >
              {{ editingLink ? 'Save changes' : 'Save link' }}
            </button>
          </template>
        </k-modal>
        <k-modal
          v-if="showDeleteModal"
          :show="showDeleteModal"
          class="learner-links-modal"
          @close="closeDeleteModal"
        >
          <template #header>
            <h3>Delete link</h3>
          </template>
          <template #body>
            <p>
              This will permanently remove the EDUKATE reference to this link.
            </p>
          </template>
          <template #footer>
            <button type="button" class="btn btn-outlined" @click="closeDeleteModal">Cancel</button>
            <button
              v-if="deletingLink"
              type="button"
              class="btn btn-danger"
              disabled
            >
              Deleting link <i class="fa-duotone fa-spinner fa-spin" aria-hidden="true"></i>
            </button>
            <button
              v-else
              type="button"
              class="btn btn-danger"
              @click="confirmDeleteLink"
            >
              Delete link
            </button>
          </template>
        </k-modal>
      </div>
    </template>
  </k-panel>
</template>

<style>
.learner-links-modal .modal-container {
  min-width: unset;
  width: calc(100% - 2rem);
  max-width: 67.5rem;
}
</style>

<style scoped>
.learner-links-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5em;
  margin-bottom: 1em;
}
</style>

<script>
import KPanel from '@base-components/k-panel.vue';
import KModal from '@base-components/k-modal.vue';
import {
  createEmptyLearnerLinkForm,
  isLearnerLinkUrlInputValid,
  learnerLinkFormFromRecord,
  learnerLinkRequestBody,
} from '@utils/learner-link-presets';
import LearnerLinksList from '@base-components/learner-links/learner-links-list.vue';
import LearnerLinksForm from './learner-links-form.vue';

export default {
  components: {
    KPanel,
    KModal,
    LearnerLinksForm,
    LearnerLinksList,
  },

  props: {
    userId: {
      type: Number,
      required: true,
    },
    programmeId: {
      type: Number,
      required: true,
    },
    isLearnerSupport: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showContent: false,
      links: [],
      savingLink: false,
      deletingLink: false,
      isGenerating: false,
      showLinkModal: false,
      showDeleteModal: false,
      editingLink: null,
      linkToDelete: null,
      linkForm: createEmptyLearnerLinkForm(),
    };
  },

  computed: {
    canManageLinks() {
      return this.$permissions.hasPermission('manage_learner_links');
    },

    showGenerateDeliveryProfile() {
      return this.$featureFlags.isEnabled('delivery-profile');
    },

    disableSaveLink() {
      return !isLearnerLinkUrlInputValid(this.linkForm.url);
    },
  },

  watch: {
    userId() {
      this.fetchLinks();
    },
    programmeId: {
      immediate: true,
      handler() {
        this.fetchLinks();
      },
    },
  },

  methods: {
    generateDeliveryProfile() {
      this.isGenerating = true;
      this.$logger.info('Generating delivery profile', undefined, true);
      this.$http.post(
        `/api/profile/users/${this.userId}/programmes/${this.programmeId}/links/delivery-profile`,
      ).then(() => {
        this.$ktoast.success('Delivery profile has been generated');
        this.$logger.info('Delivery profile generated', { userId: this.userId, programmeId: this.programmeId });
        this.fetchLinks();
      }).catch(error => {
        this.$logger.autowarn(
          'Error generating delivery profile',
          { userId: this.userId, programmeId: this.programmeId },
          error,
        );
        this.$ktoast.showError(error);
      }).then(() => {
        this.isGenerating = false;
      });
    },

    fetchLinks() {
      if (!this.userId || !this.programmeId) {
        return;
      }

      this.$http
        .get(`/api/profile/users/${this.userId}/programmes/${this.programmeId}/links`)
        .then(result => {
          this.links = result.data;
        })
        .catch(error => {
          this.$logger.error(
            'Error getting learner links',
            { userId: this.userId, programmeId: this.programmeId },
            error,
          );
          this.$ktoast.showError(error);
        });
    },

    openAddModal() {
      this.editingLink = null;
      this.linkForm = createEmptyLearnerLinkForm();
      this.showLinkModal = true;
    },

    openEditModal(link) {
      this.editingLink = link;
      this.linkForm = learnerLinkFormFromRecord(link);
      this.showLinkModal = true;
    },

    closeLinkModal() {
      this.showLinkModal = false;
      this.editingLink = null;
      this.linkForm = createEmptyLearnerLinkForm();
    },

    submitLinkForm() {
      this.$refs.linksFormComponent?.handleSubmit?.();
    },

    saveLink() {
      if (this.editingLink) {
        this.updateLink();
        return;
      }
      this.createLink();
    },

    persistLink({ method, payload, messages }) {
      this.savingLink = true;
      this.$logger.info(messages.start, undefined, true);
      this.$http[method](
        `/api/profile/users/${this.userId}/programmes/${this.programmeId}/links`,
        payload,
      ).then(() => {
        this.$ktoast.success(messages.successToast);
        this.$logger.info(messages.successLog, messages.successContext);
        this.closeLinkModal();
        this.fetchLinks();
      }).catch(error => {
        this.$logger.error(messages.errorLog, messages.errorContext, error);
        this.$ktoast.showError(error);
      }).then(() => {
        this.savingLink = false;
      });
    },

    createLink() {
      this.persistLink({
        method: 'post',
        payload: learnerLinkRequestBody(this.linkForm),
        messages: {
          start: 'Adding new learner link',
          successToast: 'Link has been added',
          successLog: 'New learner link added',
          successContext: undefined,
          errorLog: 'Error adding learner link',
          errorContext: { userId: this.userId, programmeId: this.programmeId },
        },
      });
    },

    updateLink() {
      this.persistLink({
        method: 'put',
        payload: {
          link_id: this.editingLink.id,
          ...learnerLinkRequestBody(this.linkForm),
        },
        messages: {
          start: 'Updating learner link',
          successToast: 'Link has been updated',
          successLog: 'Updated learner link',
          successContext: { linkId: this.editingLink.id },
          errorLog: 'Error updating learner link',
          errorContext: {
            userId: this.userId,
            programmeId: this.programmeId,
            linkId: this.editingLink.id,
          },
        },
      });
    },

    openDeleteModal(link) {
      this.linkToDelete = link;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.linkToDelete = null;
    },

    confirmDeleteLink() {
      this.deletingLink = true;
      this.$logger.info('Deleting learner link', undefined, true);
      this.$http.delete(
        `/api/profile/users/${this.userId}/programmes/${this.programmeId}/links/${this.linkToDelete.id}`,
      ).then(() => {
        this.$ktoast.success('Link has been deleted');
        this.$logger.info('Deleted learner link', { linkId: this.linkToDelete.id });
        this.closeDeleteModal();
        this.fetchLinks();
      }).catch(error => {
        this.$logger.error(
          'Error deleting learner link',
          { userId: this.userId, programmeId: this.programmeId, linkId: this.linkToDelete.id },
          error,
        );
        this.$ktoast.showError(error);
      }).then(() => {
        this.deletingLink = false;
      });
    },
  },
};
</script>
